

import { useState, useEffect } from 'react';
import { Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

export default function NewsletterSignup() {
  const [email, setEmail] = useState('');
  const [showSubscribeSuccess, setShowSubscribeSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);

  const [captchaToken, setCaptchaToken] = useState('');

  useEffect(() => {
    if (showCaptcha) {
      // create a turnstile element
      const div = document.getElementById('put-captcha-here');
      const widgetId = turnstile.render(div, {
        sitekey: "0x4AAAAAACHYy1fKOyxQb1_b",
        callback: function (token) {
          setCaptchaToken(token);
          setShowCaptcha(false);
        },
      });
      return () => {
        turnstile.reset(widgetId); // @ts-ignore
      }
    }
  }, [showCaptcha]);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    if (!captchaToken) {
      setEmailError('Please solve the captcha');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('https://kyvko2rs3yz3cg3xiwungmwura0jmswk.lambda-url.eu-west-2.on.aws/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, token: captchaToken }),
      });

      if (!response.ok) {
        throw new Error('Failed to sign up');
      }

      setEmail('');
      setEmailError('');
      setShowSubscribeSuccess(true);
      setTimeout(() => setShowSubscribeSuccess(false), 3000);
    } catch (error) {
      setEmailError('Failed to subscribe. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-4 py-6"
    >
      <motion.div 
        variants={container}
        initial="hidden"
        animate="show"
        className="bg-zinc-900 p-6 rounded-lg relative overflow-hidden border-2 border-white/30 shadow-[0_0_15px_rgba(255,255,255,0.1)] transition-all duration-300 hover:border-white/50 hover:shadow-[0_0_20px_rgba(255,255,255,0.2)]"
      >
        <AnimatePresence>
          {showSubscribeSuccess && (
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="absolute top-0 left-0 right-0 bg-green-500 text-white p-2 text-center"
            >
              Bedankt voor je aanmelding!
            </motion.div>
          )}
        </AnimatePresence>
        
        <motion.div 
          variants={item}
          className="flex gap-4 items-center mb-4"
        >
          <motion.div 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="w-12 h-12 bg-zinc-800 rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-zinc-700"
          >
            <motion.div
              whileHover={{ rotate: 15 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <Mail className="w-6 h-6" />
            </motion.div>
          </motion.div>
          <motion.div variants={item}>
            <h3 className="font-medium text-lg">Ontvang updates direct van Fresku</h3>
          </motion.div>
        </motion.div>
        
        <motion.form 
          variants={item}
          onSubmit={handleSubscribe} 
          className="space-y-2"
        >
          <div className="flex flex-col sm:flex-row gap-2">
            <motion.input
              whileFocus={{ scale: 1.02 }}
              type="email"
              placeholder="jouw@email.nl"
              value={email}
              onFocus={() => {
                setShowCaptcha(true);
              }}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError('');
              }}
              className={`flex-1 bg-zinc-800 rounded-lg px-6 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/30 transition-all duration-300 hover:bg-zinc-700 ${
                emailError ? 'border-red-500' : ''
              }`}
            />
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit" 
              disabled={isLoading}
              className={`px-6 py-3 bg-white text-black font-medium rounded-lg transition-all duration-300 whitespace-nowrap w-1/2 mx-auto sm:w-auto sm:mx-0 ${
                isLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200 hover:shadow-lg'
              }`}
            >
              {isLoading ? 'Bezig met aanmelden...' : 'Aanmelden'}
            </motion.button>
            
          </div>
          {showCaptcha && (
            <div className="mt-4 w-full">
              <div id="put-captcha-here" className="float-right "></div>
            </div>
            )}
          <AnimatePresence>
            {emailError && (
              <motion.p 
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="text-red-500 text-sm"
              >
                {emailError}
              </motion.p>
            )}
          </AnimatePresence>
        </motion.form>
        
        <motion.p 
          variants={item}
          className="text-xs text-gray-400 mt-4 text-center"
        >
          Door je aan te melden ga je akkoord met het ontvangen van marketing e-mails en accepteer je ons Privacybeleid.
        </motion.p>
      </motion.div>
    </motion.div>
  );
}
